<template>
    <div class="rr-tbp-root">
        <div v-if="loading" class="rr-loading"><v-progress-circular indeterminate color="primary" size="26" width="3" /></div>
        <div v-else class="rr-tbp-grid" :style="gridStyle">
            <div v-for="panel in panels" :key="panel.key" class="rr-tbp-card">
                <div class="rr-tbp-head">
                    <span v-if="panel.logo" class="rr-platform-logo" v-html="panel.logo"></span>
                    <span class="rr-tbp-name">{{ panel.label }}</span>
                </div>
                <div v-if="panel.rows.length" class="rr-tbp-rows">
                    <div v-for="row in panel.rows" :key="panel.key + '-' + row.canonicalName" class="rr-tbp-row" :class="{ 'rr-tbp-row--primary': row.isPrimary }">
                        <div class="rr-tbp-rank" :class="'rr-rank-' + Math.min(row.rank, 4)">#{{ row.rank }}</div>
                        <div class="rr-tbp-main">
                            <div class="rr-tbp-brand">{{ row.canonicalName }}</div>
                            <div class="rr-tbp-meta">{{ row.mentions }} Mentions · Sent {{ row.sentText }}</div>
                        </div>
                        <div class="rr-tbp-metric">
                            <div class="rr-tbp-vis">{{ row.vis.toFixed(2) }}%</div>
                            <div v-if="row.visDelta != null" class="rr-delta-chip" :class="{ up: row.visDelta > 0, down: row.visDelta < 0 }">
                                <v-icon x-small>{{ row.visDelta > 0 ? "mdi-arrow-up" : row.visDelta < 0 ? "mdi-arrow-down" : "mdi-minus" }}</v-icon>
                                {{ (row.visDelta > 0 ? "+" : "") + row.visDelta.toFixed(2) }}%
                            </div>
                            <div class="rr-bar"><div class="rr-bar-fill" :style="{ width: row.barPct + '%' }"></div></div>
                        </div>
                    </div>
                </div>
                <div v-else class="rr-empty">No data</div>
            </div>
        </div>
    </div>
</template>

<script>
    import { fetchRangeAverages, computeRanges, isPrimaryBrand } from "../reportApi"
    import { getPlatformMeta } from "../../../utils/platformMeta"

    export default {
        name: "TopBrandsSection",
        props: {
            filters: { type: Object, required: true },
            companyId: { type: String, required: true },
            projectId: { type: String, required: true },
            project: { type: Object, default: null },
            brands: { type: Array, default: () => [] },
            platformKeys: { type: Array, default: () => [] }
        },
        data() {
            return { loading: true, panels: [] }
        },
        computed: {
            // Balance the grid so the last panel never wraps onto a row by itself.
            // With max 4 columns: 5 panels -> 3+2, 6 -> 3+3, 7 -> 4+3, 8 -> 4+4, etc.
            gridStyle() {
                const n = this.panels.length
                if (n <= 1) return {}
                const maxCols = 4
                const rows = Math.ceil(n / maxCols)
                const cols = Math.ceil(n / rows)
                return { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }
            }
        },
        methods: {
            buildPanel(key, rows) {
                const meta = getPlatformMeta(key)
                const valid = rows.filter((b) => typeof b.visibilityPercent === "number" && b.visibilityPercent > 0).sort((a, b) => (b.visibilityPercent || 0) - (a.visibilityPercent || 0))
                const maxVis = Math.max(1, ...valid.map((b) => b.visibilityPercent || 0))
                const toRow = (b, rank) => ({
                    canonicalName: b.canonicalName || b.brand_name,
                    rank,
                    mentions: b.responsesWithMention || 0,
                    sentText: typeof b.sentimentAvg === "number" ? (b.sentimentAvg * 100 > 0 ? "+" : "") + (b.sentimentAvg * 100).toFixed(0) : "—",
                    vis: b.visibilityPercent || 0,
                    visDelta: typeof b.visibilityPercentOld === "number" ? (b.visibilityPercent || 0) - b.visibilityPercentOld : null,
                    barPct: Math.max(4, ((b.visibilityPercent || 0) / maxVis) * 100),
                    isPrimary: isPrimaryBrand(b, this.project)
                })
                const rowsOut = valid.slice(0, 5).map((b, i) => toRow(b, i + 1))
                // if the project's own brand ranks below top 5, append it like the dashboard does
                const primaryIdx = valid.findIndex((b) => isPrimaryBrand(b, this.project))
                if (primaryIdx >= 5) rowsOut.push(toRow(valid[primaryIdx], primaryIdx + 1))
                return { key, label: meta.label || key, logo: meta.logo, rows: rowsOut }
            },
            async fetchAll() {
                this.loading = true
                try {
                    const ranges = computeRanges(this.filters.selectedDates)
                    if (!ranges) {
                        this.panels = []
                        return
                    }
                    const results = await Promise.all(
                        this.platformKeys.map((key) =>
                            fetchRangeAverages(this.companyId, {
                                projectId: this.projectId,
                                platform: key,
                                ranges,
                                brands: this.brands,
                                tags: this.filters.tags,
                                tagsOperator: this.filters.tagsOperator
                            }).catch(() => [])
                        )
                    )
                    this.panels = this.platformKeys.map((key, i) => this.buildPanel(key, results[i]))
                } catch (e) {
                    this.panels = []
                } finally {
                    this.loading = false
                    this.$emit("ready")
                }
            }
        },
        mounted() {
            this.fetchAll()
        }
    }
</script>
